// AudioMiniPlayer — глобальный sticky bottom-bar плеер. Появляется когда
// recapId set в store (юзер кликнул «Слушать» на карточке recap'а или
// продолжается воспроизведение между страницами). Скрыт на `/audio-recaps/$id`
// — там есть полноценный RecapPlayerBar и дублировать не надо.
//
// Минимальный UI: title (кликабельный → recap detail), play/pause, scrubber
// по waveform-like прогрессу, cycle-скорость (1→1.25→1.5→2, persist), close.
// Громкость — в детальном плеере.

import { Button, DUR_FAST_MS, Tooltip, TooltipContent, TooltipTrigger } from "@studyhub/ui";
import {
	IconPlayerPauseFilled,
	IconPlayerPlayFilled,
	IconRewindBackward15,
	IconRewindForward15,
	IconX,
} from "@tabler/icons-react";
import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PEAKS_RESOLUTION, Waveform } from "@/components/audio-recaps/_waveform";
import { formatDurationHms } from "@/components/recordings/_format";
import { useAudioPeaks } from "@/hooks/use-audio-peaks";
import { cn } from "@/lib/cn";
import { useIsSmUp } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { formatAudioPlaybackRate, useAudioRecapStore } from "@/stores/audio-recap";
import { useSupportWidgetObstruction } from "@/stores/support-widget";
import { isWorkspaceRoute } from "./_layout-state";

// CSS-переменная высоты плеера. Ставится на <html>, чтобы любой fixed/sticky/
// scroll-контейнер (main, композеры чата, sticky-сайдбары) мог зарезервировать
// под собой отступ. Когда плеер скрыт — `0px` (см. setAudioPlayerHeightVar).
// Аналог --sidebar-width / --side-panel-width / --keyboard-height.
const AUDIO_PLAYER_HEIGHT_VAR = "--audio-player-height";

function setAudioPlayerHeightVar(pxValue: number): void {
	if (typeof document === "undefined") return;
	document.documentElement.style.setProperty(AUDIO_PLAYER_HEIGHT_VAR, `${pxValue}px`);
}

function clearAudioPlayerHeightVar(): void {
	if (typeof document === "undefined") return;
	document.documentElement.style.setProperty(AUDIO_PLAYER_HEIGHT_VAR, "0px");
}

export function AudioMiniPlayer(): React.ReactElement | null {
	const { t } = useTranslation("audio");
	const recapId = useAudioRecapStore((s) => s.recapId);
	const trackTitle = useAudioRecapStore((s) => s.trackTitle);
	const positionSec = useAudioRecapStore((s) => s.positionSec);
	const durationSec = useAudioRecapStore((s) => s.durationSec);
	const isPlaying = useAudioRecapStore((s) => s.isPlaying);
	const speed = useAudioRecapStore((s) => s.speed);
	const toggle = useAudioRecapStore((s) => s.toggle);
	const seek = useAudioRecapStore((s) => s.seek);
	const cycleSpeed = useAudioRecapStore((s) => s.cycleSpeed);
	const close = useAudioRecapStore((s) => s.close);

	const location = useLocation();
	// На детальной странице recap'а — полноценный RecapPlayerBar; mini-player
	// дублировал бы UI и Audio-engine хост. Скрываем.
	const isOnRecapDetail =
		location.pathname.startsWith("/audio-recaps/") &&
		!location.pathname.endsWith("/audio-recaps/") &&
		!location.pathname.endsWith("/audio-recaps") &&
		!location.pathname.startsWith("/audio-recaps/new") &&
		!location.pathname.startsWith("/audio-recaps/share");

	// В воркспейсе курса свой сквозной плеер живёт в потоке сцены (RecapPlayerBar,
	// декрет E7). Без этого гейта на экране было бы ДВА плеера, а высота глобального
	// вычиталась бы из h-fit-app поверх высоты плеера сцены (двойной вычет).
	const isInCourseWorkspace = isWorkspaceRoute(location.pathname);
	/** Волна и тайминги — только с `sm` (ниже ряд не сходится, см. разметку). */
	const showWaveform = useIsSmUp();

	const isVisible = !!recapId && !isOnRecapDetail && !isInCourseWorkspace;

	// Уход из курса при СТОЯЩЕМ плеере закрывает его совсем.
	//
	// Внутри курса плеер темы виден только пока играет (`scene-audio-dock.tsx`), а
	// на detail-роуте то же правило действует при размонтировании
	// (`routes/audio-recaps/$id.tsx`). Без этой ветки поставленное на паузу
	// резюме выныривало полосой на главной и в библиотеке, хотя человек его уже
	// закрыл вместе с темой (жалоба 2026-08-13). Правило живёт ЗДЕСЬ: гейт
	// видимости в курсе — тоже здесь, и второе место для одного решения
	// разъехалось бы с первым.
	const wasInWorkspaceRef = useRef(isInCourseWorkspace);
	useEffect(() => {
		const leftWorkspace = wasInWorkspaceRef.current && !isInCourseWorkspace;
		wasInWorkspaceRef.current = isInCourseWorkspace;
		// Уход НА полный плеер резюме — исключение: там человек продолжает слушать
		// то же самое, и сброшенная позиция заставила бы искать место заново.
		if (!leftWorkspace || isOnRecapDetail) return;
		const state = useAudioRecapStore.getState();
		if (state.recapId && !state.isPlaying) state.close();
	}, [isInCourseWorkspace, isOnRecapDetail]);

	// Держим DOM на время CSS-exit: скрытие на detail/workspace и закрытие
	// (recapId → null) иначе щёлкают, потому что `return null` рвёт transition.
	const [heldRecapId, setHeldRecapId] = useState(recapId);
	const [barReady, setBarReady] = useState(false);
	if (recapId && recapId !== heldRecapId) {
		setHeldRecapId(recapId);
	}
	useEffect(() => {
		if (recapId) return;
		const reduced =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const delay = reduced ? 0 : DUR_FAST_MS;
		const timer = window.setTimeout(() => setHeldRecapId(null), delay);
		return () => window.clearTimeout(timer);
	}, [recapId]);

	const mounted = !!(recapId ?? heldRecapId);
	useEffect(() => {
		if (!mounted) {
			setBarReady(false);
			return;
		}
		const id = requestAnimationFrame(() => setBarReady(true));
		return () => cancelAnimationFrame(id);
	}, [mounted]);

	const showBar = isVisible && barReady;
	// Плеер тянется до правого края — кнопка «Поддержка» легла бы на его контролы.
	useSupportWidgetObstruction(showBar);
	const linkRecapId = recapId ?? heldRecapId ?? "";

	// Peaks / getStreamUrl только когда бар реально виден. В workspace UI скрыт,
	// но хуки раньше всё равно качали полный MP3 (~12 MB) ради waveform.
	// Playback идёт через AudioPlayerEngine / useMiniPlayer — не через этот query.
	const streamQuery = api.audioRecaps.recaps.getStreamUrl.useQuery(
		{ recapId: recapId ?? "" } as never,
		{ enabled: !!recapId && isVisible, staleTime: 50 * 60 * 1000, retry: false },
	);
	const streamUrl = (streamQuery.data as { url?: string } | undefined)?.url ?? null;
	// Волна живёт только с `sm` — ниже её нет в разметке. Не качаем полный MP3
	// ради пиков, которых никто не увидит (мобильный трафик).
	const peaks = useAudioPeaks(isVisible && showWaveform ? streamUrl : null, PEAKS_RESOLUTION);

	const panelRef = useRef<HTMLDivElement | null>(null);
	// Публикуем фактическую высоту панели в --audio-player-height на <html>,
	// чтобы остальной интерфейс (main, композеры чата, sticky-сайдбары)
	// зарезервировал под плеером отступ и контент не уходил под него.
	// ResizeObserver — высота меняется при ресайзе viewport / появлении
	// safe-area. При скрытии плеера переменная сбрасывается в 0px.
	useEffect(() => {
		if (!isVisible) {
			clearAudioPlayerHeightVar();
			return;
		}
		const panel = panelRef.current;
		if (!panel) return;
		const publish = (): void => setAudioPlayerHeightVar(panel.offsetHeight);
		publish();
		const observer = new ResizeObserver(publish);
		observer.observe(panel);
		return () => {
			observer.disconnect();
			clearAudioPlayerHeightVar();
		};
	}, [isVisible]);

	if (!mounted) return null;

	return (
		<div
			ref={panelRef}
			data-audio-mini-player
			aria-hidden={!showBar}
			inert={!showBar ? true : undefined}
			className={cn(
				"fixed z-(--sh-z-chrome-top)",
				"border-t border-border bg-card/95 backdrop-blur-md",
				"transition-[transform,opacity,left,right,bottom] duration-fast ease-spring motion-reduce:transition-none",
				showBar ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0",
				// На mobile плеер встаёт НАД bottom-nav (его высота 4rem + safe-area),
				// чтобы оба были видны и не перекрывали друг друга. На desktop
				// (lg:) bottom-nav нет — плеер прижат к низу экрана + safe-area.
				"bottom-[calc(var(--bottom-nav-height,4rem)+env(safe-area-inset-bottom,0px))] lg:bottom-0",
				"lg:pb-[env(safe-area-inset-bottom,0px)]",
			)}
			// inline style: var(--sidebar-width)/--side-panel-width задаются в
			// MainLayout (см. main-layout.tsx) и обновляются при collapse sidebar
			// / открытии tutor-panel. paddingBottom (safe-area) нужен только на
			// desktop — на mobile safe-area уже зарезервирована под bottom-nav.
			style={{
				left: "var(--sidebar-width, 0px)",
				right: "var(--side-panel-width, 0px)",
			}}
		>
			{/*
				Полоса прогресса — только ниже `sm`, где волны нет (см. ниже). Без неё
				на телефоне не видно ни позиции, ни длины трека.
			*/}
			<div
				className="h-0.5 w-full bg-muted sm:hidden"
				// Не `aria-hidden`: ниже `sm` тайминги скрыты вместе с волной, и это
				// единственный источник позиции для скринридера.
				role="progressbar"
				aria-label={t("miniPlayer.position")}
				aria-valuemin={0}
				aria-valuemax={Math.max(1, Math.round(durationSec))}
				aria-valuenow={Math.round(positionSec)}
				aria-valuetext={formatDurationHms(positionSec)}
			>
				<div
					className="h-full bg-primary transition-[width] duration-base ease-out motion-reduce:transition-none"
					style={{
						width: `${durationSec > 0 ? Math.min(100, (positionSec / durationSec) * 100) : 0}%`,
					}}
				/>
			</div>
			<div className="flex items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
				{/* Title — кликабельный, ведёт в детальную страницу */}
				<Link
					to="/audio-recaps/$id"
					params={{ id: linkRecapId }}
					// Ниже `sm` заголовок забирает всю свободную ширину (волны там нет),
					// но `min-w-0 truncate` не даёт ему выдавить транспорт и крестик: на
					// узком экране крестик уезжал за край (жалоба 2026-08-13).
					className="min-w-0 flex-1 truncate text-sm font-medium text-foreground hover:text-primary transition-colors duration-fast cursor-pointer sm:max-w-[280px] sm:flex-none sm:shrink"
					title={trackTitle ?? ""}
				>
					{trackTitle ?? t("miniPlayer.defaultTitle")}
				</Link>

				{/* Transport. На mobile тач-кнопки крупнее (h-10 ≈ 40px), на
				    desktop (sm:) — компактные h-8, как было. Зазор на телефоне
				    минимальный: ряд обязан оставить место крестику. */}
				<div className="flex items-center gap-0.5 shrink-0">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => seek(Math.max(0, positionSec - 15))}
								aria-label={t("player.skipBack")}
								className="cursor-pointer h-10 w-10 sm:h-8 sm:w-8"
							>
								<IconRewindBackward15 size={16} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top">{t("player.skipBack")}</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								size="icon"
								onClick={toggle}
								aria-label={isPlaying ? t("player.pause") : t("player.play")}
								className="cursor-pointer rounded-full h-10 w-10 sm:h-8 sm:w-8"
							>
								{isPlaying ? (
									<IconPlayerPauseFilled size={14} />
								) : (
									<IconPlayerPlayFilled size={14} />
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top">
							{isPlaying ? t("player.pause") : t("player.play")}
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => seek(Math.min(durationSec, positionSec + 15))}
								aria-label={t("player.skipForward")}
								className="cursor-pointer h-10 w-10 sm:h-8 sm:w-8"
							>
								<IconRewindForward15 size={16} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top">{t("player.skipForward")}</TooltipContent>
					</Tooltip>
					{/* Скорость — только с `sm`: на телефоне её место нужнее крестику,
					    а сама скорость меняется в полном плеере (тап по названию). */}
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={() => cycleSpeed()}
								aria-label={t("miniPlayer.speedAria", {
									rate: formatAudioPlaybackRate(speed),
								})}
								className="hidden h-10 min-w-10 shrink-0 cursor-pointer items-center justify-center rounded-full px-1.5 text-xs font-medium tabular-nums text-foreground transition-colors hover:bg-muted sm:inline-flex sm:h-8 sm:min-w-8 sm:text-[11px]"
							>
								{formatAudioPlaybackRate(speed)}
							</button>
						</TooltipTrigger>
						<TooltipContent side="top">{t("player.speed")}</TooltipContent>
					</Tooltip>
				</div>

				{/*
					Waveform-scrubber: канвас как в RecapPlayerBar, click + drag.
					Ниже `sm` его нет вовсе: на 360px ряд «название + транспорт + волна +
					тайминги + крестик» не сходился, волна сжималась в ноль, а крестик
					уезжал за край (жалоба 2026-08-13). Позицию там показывает тонкая
					полоса прогресса над рядом, перемотка — ±15 сек и полный плеер.
				*/}
				<div className="hidden min-w-0 flex-1 items-center gap-2 sm:flex sm:gap-3">
					<span className="text-2xs tabular-nums text-muted-foreground text-right sm:min-w-[40px]">
						{formatDurationHms(positionSec)}
					</span>
					<div className="flex-1 relative min-w-0">
						{/* `active` — волна не перерисовывается, пока плеер скрыт или
						    уезжает: в workspace/detail она не видна, а во время exit-
						    анимации последний кадр должен остаться на месте. */}
						<Waveform
							peaks={peaks}
							positionSec={positionSec}
							durationSec={durationSec}
							onSeek={seek}
							active={isVisible && showWaveform}
							className="h-8 sm:h-6"
						/>
					</div>
					<span className="text-2xs tabular-nums text-muted-foreground min-w-[40px]">
						{formatDurationHms(durationSec)}
					</span>
				</div>

				{/* Close */}
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={close}
							aria-label={t("miniPlayer.close")}
							className="cursor-pointer h-10 w-10 shrink-0 sm:h-8 sm:w-8"
						>
							<IconX size={16} className="sm:size-3.5" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="top">{t("miniPlayer.close")}</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);
}
